import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@repo/common/utils/cn";

const badgeVariants = cva(
  "inline-flex items-center flex-shrink-0 rounded-full border px-2 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 h-min",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-action-primary text-action-primary-foreground hover:bg-action-primary-hover",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        destructive:
          "border-transparent bg-status-destructive text-white hover:bg-status-error-darken-1",
        outline: "text-foreground",
      },
      size: {
        default: "py-0.5",
        sm: "py-0",
      },
      intent: {
        success:
          "text-status-success-darken-2 bg-status-success-lighten-3 border-status-success-lighten-2 hover:bg-status-success-lighten-2",
        warn: "text-status-warning-darken-2 bg-status-warning-lighten-3 border-status-warning-lighten-2 hover:bg-status-warning-lighten-2",
        danger:
          "text-status-error-darken-2 bg-status-error-lighten-3 border-status-error-lighten-2 hover:bg-status-error-lighten-2",
        neutral:
          "text-status-neutral-darken-2 bg-status-neutral-lighten-3 border-status-neutral-lighten-2 hover:bg-status-neutral-lighten-2",
        callout:
          "border-primary-lighten-2 bg-primary-lighten-3 text-action-primary-darken-2 hover:bg-primary-lighten-2",
        recommended:
          "border-lime-300 bg-lime-100 text-lime-950 hover:bg-lime-100",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, intent, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size, intent }), className)}
      data-testid={`badge-${intent}`}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
